<template>
    <div v-if="show" 
         class="fixed inset-0 bg-black/55 z-50 flex items-center justify-center p-4"
         @click="handleBackdropClick">
        <div class="foxy-modal max-w-md mx-4 max-h-[calc(100vh-2rem)] overflow-hidden flex flex-col"
             @click.stop>
            <!-- Header -->
            <div class="foxy-modal-section p-6 pb-4 sm:pb-6 border-b sticky top-0 bg-foxy-surface/95 backdrop-blur z-10">
                <div class="flex items-center justify-between">
                    <h3 class="text-2xl font-bold text-foxy-text">Додати до списку</h3>
                    <button @click="closeModal"
                            class="foxy-icon-button !h-6 !w-6">
                        <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>
            </div>

            <!-- Content (scrollable on small screens) -->
            <div class="p-6 pt-4 sm:pt-6 overflow-y-auto overscroll-contain">
                <!-- Status Options -->
                <div class="space-y-3">
                    <!-- Remove current status -->
                    <button v-if="currentStatus" @click="removeCurrentStatus"
                            class="w-full text-left p-4 rounded-xl border-2 border-transparent hover:border-foxy-error transition-all duration-300 bg-foxy-elevated hover:bg-foxy-error/10 group">
                        <div class="flex items-center space-x-4">
                            <div class="w-12 h-12 bg-foxy-error/10 rounded-xl flex items-center justify-center mr-4 group-hover:bg-foxy-error/20 transition-colors">
                                <svg class="w-6 h-6 text-foxy-error" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M18 12H6" />
                                </svg>
                            </div>
                            <div class="flex-1">
                                <div class="flex items-center justify-between">
                                    <h4 class="text-lg font-bold text-foxy-text group-hover:text-foxy-error transition-colors">
                                        Прибрати статус</h4>
                                </div>
                                <p class="text-sm text-foxy-text-secondary">Зробити книгу без статусу читання</p>
                            </div>
                        </div>
                    </button>

                    <!-- Divider -->
                    <div v-if="currentStatus" class="border-t border-foxy-border my-4"></div>

                    <!-- Прочитано -->
                    <button @click="selectStatus('read')"
                            :class="[
                                'w-full text-left p-4 rounded-xl border-2 transition-all duration-300 group',
                                currentStatus === 'read' 
                                    ? 'border-foxy-success bg-foxy-success/10'
                                    : 'border-transparent hover:border-foxy-success bg-foxy-elevated hover:bg-foxy-success/10'
                            ]">
                        <div class="flex items-center space-x-4">
                            <div class="w-12 h-12 bg-foxy-success/10 rounded-xl flex items-center justify-center mr-4 group-hover:bg-foxy-success/20 transition-colors">
                                <svg class="w-6 h-6 text-foxy-success" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                                </svg>
                            </div>
                            <div class="flex-1">
                                <div class="flex items-center justify-between">
                                    <h4 class="text-lg font-bold text-foxy-text group-hover:text-foxy-success transition-colors">
                                        Прочитано</h4>
                                    <span v-if="currentStatus === 'read'" class="text-foxy-success text-sm font-medium">
                                        Обрано
                                    </span>
                                </div>
                                <p class="text-sm text-foxy-text-secondary">Книга прочитана повністю</p>
                            </div>
                        </div>
                    </button>

                    <!-- Читаю -->
                    <button @click="selectStatus('reading')"
                            :class="[
                                'w-full text-left p-4 rounded-xl border-2 transition-all duration-300 group',
                                currentStatus === 'reading' 
                                    ? 'border-blue-500 dark:border-blue-400 bg-blue-50 dark:bg-blue-900/30'
                                    : 'border-transparent hover:border-blue-500 dark:hover:border-blue-400 bg-foxy-elevated hover:bg-blue-50 dark:hover:bg-blue-900/20'
                            ]">
                        <div class="flex items-center space-x-4">
                            <div class="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-xl flex items-center justify-center mr-4 group-hover:bg-blue-200 dark:group-hover:bg-blue-800/40 transition-colors">
                                <svg class="w-6 h-6 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                                </svg>
                            </div>
                            <div class="flex-1">
                                <div class="flex items-center justify-between">
                                    <h4 class="text-lg font-bold text-foxy-text group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                                        Читаю</h4>
                                    <span v-if="currentStatus === 'reading'" class="text-blue-600 dark:text-blue-400 text-sm font-medium">
                                        Обрано
                                    </span>
                                </div>
                                <p class="text-sm text-foxy-text-secondary">Зараз читаю цю книгу</p>
                            </div>
                        </div>
                    </button>

                    <!-- Буду читать -->
                    <button @click="selectStatus('want-to-read')"
                            :class="[
                                'w-full text-left p-4 rounded-xl border-2 transition-all duration-300 group',
                                currentStatus === 'want_to_read' 
                                    ? 'border-purple-500 dark:border-purple-400 bg-purple-50 dark:bg-purple-900/30'
                                    : 'border-transparent hover:border-purple-500 dark:hover:border-purple-400 bg-foxy-elevated hover:bg-purple-50 dark:hover:bg-purple-900/20'
                            ]">
                        <div class="flex items-center space-x-4">
                            <div class="w-12 h-12 bg-purple-100 dark:bg-purple-900/30 rounded-xl flex items-center justify-center mr-4 group-hover:bg-purple-200 dark:group-hover:bg-purple-800/40 transition-colors">
                                <svg class="w-6 h-6 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                                </svg>
                            </div>
                            <div class="flex-1">
                                <div class="flex items-center justify-between">
                                    <h4 class="text-lg font-bold text-foxy-text group-hover:text-purple-600 dark:group-hover:text-purple-400 transition-colors">
                                        Буду читати</h4>
                                    <span v-if="currentStatus === 'want_to_read'" class="text-purple-600 dark:text-purple-400 text-sm font-medium">
                                        Обрано
                                    </span>
                                </div>
                                <p class="text-sm text-foxy-text-secondary">Планую прочитати цю книгу</p>
                            </div>
                        </div>
                    </button>

                    <!-- Не дочитано -->
                    <button @click="selectStatus('abandoned')"
                            :class="[
                                'w-full text-left p-4 rounded-xl border-2 transition-all duration-300 group',
                                currentStatus === 'abandoned' 
                                    ? 'border-foxy-error bg-foxy-error/10'
                                    : 'border-transparent hover:border-foxy-error bg-foxy-elevated hover:bg-foxy-error/10'
                            ]">
                        <div class="flex items-center space-x-4">
                            <div class="w-12 h-12 bg-foxy-error/10 rounded-xl flex items-center justify-center mr-4 group-hover:bg-foxy-error/20 transition-colors">
                                <svg class="w-6 h-6 text-foxy-error" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </div>
                            <div class="flex-1">
                                <div class="flex items-center justify-between">
                                    <h4 class="text-lg font-bold text-foxy-text group-hover:text-foxy-error transition-colors">
                                        Не дочитано</h4>
                                    <span v-if="currentStatus === 'abandoned'" class="text-foxy-error text-sm font-medium">
                                        Обрано
                                    </span>
                                </div>
                                <p class="text-sm text-foxy-text-secondary">Перестав читати цю книгу</p>
                            </div>
                        </div>
                    </button>

                    <!-- Divider -->
                    <div class="border-t border-foxy-border my-4"></div>

                    <!-- Add to Library Button -->
                    <button @click="openCustomLibraryModal"
                            class="w-full text-left p-4 rounded-xl border-2 border-transparent hover:border-foxy-brand transition-all duration-300 bg-foxy-elevated hover:bg-foxy-brand/10 group">
                        <div class="flex items-center space-x-4">
                            <div class="w-12 h-12 bg-foxy-brand/10 rounded-xl flex items-center justify-center mr-4 group-hover:bg-foxy-brand/20 transition-colors">
                                <svg class="w-6 h-6 text-foxy-brand" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                                </svg>
                            </div>
                            <div>
                                <h4 class="text-lg font-bold text-foxy-text group-hover:text-foxy-brand transition-colors">
                                    Додати до добірки</h4>
                                <p class="text-sm text-foxy-text-secondary">Створити або додати до існуючої добірки</p>
                            </div>
                        </div>
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'AddToLibraryModal',
    props: {
        show: {
            type: Boolean,
            default: false
        },
        book: {
            type: Object,
            required: true
        },
        userLibraries: {
            type: Array,
            default: () => []
        },
        currentStatus: {
            type: String,
            default: null
        }
    },
    mounted() {
        // Переносим модалку в body, чтобы избежать конфликтов с overflow/трансформациями родительских контейнеров (например, на головній сторінці)
        this.moveToBody();

        // Удаляем модалку из body при уничтожении компонента
        this.$once('hook:beforeDestroy', () => {
            this.removeFromBody();
        });
    },
    methods: {
        closeModal() {
            this.$emit('close');
        },
        handleBackdropClick(event) {
            if (event.target === event.currentTarget) {
                this.closeModal();
            }
        },
        selectStatus(status) {
            this.$emit('status-selected', status);
        },
        removeCurrentStatus() {
            this.$emit('remove-status');
        },
        openCustomLibraryModal() {
            // Эмитим событие для открытия модального окна с кастомными библиотеками
            this.$emit('open-custom-library');
        },
        moveToBody() {
            const modalElement = this.$el;
            if (modalElement && modalElement.parentNode !== document.body) {
                document.body.appendChild(modalElement);
            }
        },
        removeFromBody() {
            const modalElement = this.$el;
            if (modalElement && modalElement.parentNode === document.body) {
                document.body.removeChild(modalElement);
            }
        }
    }
}
</script>
